import {
  collection, addDoc, serverTimestamp, doc, updateDoc, deleteDoc
} from 'firebase/firestore'
import { db } from '../config/firebase'
import { workersAPI, projectsAPI, salesAPI, trainingAPI, adminAuthAPI, clearAdminSession } from '../services/api'
import { EMPTY_WORKER, EMPTY_TRAINING, EMPTY_EQUIP, EMPTY_PROJECT, EMPTY_SALE } from './admin/constants'
const EQUIPMENT_KEY = 'equipment_items'

function genId() {
  return Date.now().toString(36) + Math.random().toString(36).substr(2)
}
function slugify(v) {
  return v.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '')
}
function ensureUniqueSlug(base, existing) {
  if (!existing.includes(base)) return base
  let n = 2
  while (existing.includes(`${base}-${n}`)) n++
  return `${base}-${n}`
}

export function createAdminHandlers(d) {
  const {
    password,
    setPassword,
    setIsAuthenticated,
    setAuthError,
    workerForm,
    setWorkers,
    setWorkerForm,
    setWorkerStatus,
    workers,
    setEditWorker,
    setEditForm,
    editForm,
    editWorker,
    trainingForm,
    setTraining,
    setTrainingForm,
    setTrainingStatus,
    setAssignments,
    assignments,
    training,
    assignSelected,
    setAssignModal,
    setAssignSelected,
    equipmentForm,
    existingSlugs,
    uploadedItems,
    setUploadedItems,
    onAdd,
    setEquipmentForm,
    setEquipmentStatus,
    projectForm,
    setProjects,
    setProjectForm,
    setProjectStatus,
    activeProject,
    setActiveProject,
    adminCommentText,
    setAdminCommentText,
    saleForm,
    setSales,
    setSaleForm,
    setSaleStatus,
    setSelectedSale,
    selectedSale,
    setChatActiveSession,
    setChatSessionMsgs,
    adminReply,
    chatActiveSession,
    setAdminReply,
    itReply,
    itActiveWorker,
    itConversations,
    setItReply,
    adminTimeOffRequests,
    setTimeOffActionMsg,
    setActiveTab,
    setSidebarOpen
  } = d

  const handleLogin = async e => {
    e.preventDefault()
    const email = (d.adminEmail || '').trim()
    if (!email || !password) {
      setAuthError('Email and password are required.')
      return
    }
    try {
      setAuthError('')
      await adminAuthAPI.login(email, password)
      setIsAuthenticated(true)
      setPassword('')
    } catch (err) {
      setAuthError(err.message || 'Invalid credentials.')
      setIsAuthenticated(false)
    }
  }

  const handleLogout = async () => {
    await adminAuthAPI.logout()
    clearAdminSession()
    setIsAuthenticated(false)
  }

  const handleAddWorker = async e => {
    e.preventDefault()
    const { name, role } = workerForm
    if (!name.trim() || !role.trim()) { setWorkerStatus('Name and role are required.'); return }
    try {
      const created = await workersAPI.create({ ...workerForm, name: name.trim(), role: role.trim() })
      setWorkers(prev => [...prev, created])
      setWorkerForm({ ...EMPTY_WORKER }); setWorkerStatus(`Worker "${name.trim()}" added!`)
    } catch (err) { setWorkerStatus('Error adding worker: ' + err.message) }
  }
  const handleDeleteWorker = async id => {
    try {
      await workersAPI.delete(id)
      setWorkers(prev => prev.filter(w => w.id !== id))
    } catch (err) { console.error(err) }
  }
  const toggleWorkerStatus = async id => {
    const w = workers.find(w => w.id === id)
    if (!w) return
    try {
      const updated = await workersAPI.update(id, { status: w.status === 'active' ? 'inactive' : 'active' })
      setWorkers(prev => prev.map(x => x.id === id ? updated : x))
    } catch (err) { console.error(err) }
  }
  const toggleChatAccess = async id => {
    const w = workers.find(w => w.id === id)
    if (!w) return
    try {
      const updated = await workersAPI.update(id, { chat_access: !w.chat_access })
      setWorkers(prev => prev.map(x => x.id === id ? updated : x))
    } catch (err) { console.error(err) }
  }
  const openEditWorker = w => { setEditWorker(w); setEditForm({ ...w }) }
  const handleSaveWorker = async e => {
    e.preventDefault()
    if (!editForm.name?.trim() || !editForm.role?.trim()) return
    try {
      const { id, ...fields } = editForm
      const updated = await workersAPI.update(editWorker.id, { ...fields, name: editForm.name.trim(), role: editForm.role.trim() })
      setWorkers(prev => prev.map(w => w.id === editWorker.id ? updated : w))
      setEditWorker(null)
    } catch (err) { console.error(err) }
  }

  const handleAddTraining = async e => {
    e.preventDefault()
    const { title, description } = trainingForm
    if (!title.trim() || !description.trim()) { setTrainingStatus('Title and description required.'); return }
    try {
      const created = await trainingAPI.create({ ...trainingForm, title: title.trim(), description: description.trim() })
      setTraining(prev => [...prev, created])
      setTrainingForm({ ...EMPTY_TRAINING }); setTrainingStatus(`Course "${title.trim()}" added!`)
    } catch (err) { setTrainingStatus('Error: ' + err.message) }
  }
  const handleDeleteTraining = async id => {
    try {
      await trainingAPI.delete(id)
      setTraining(prev => prev.filter(t => t.id !== id))
      setAssignments(prev => prev.filter(a => (a.course_id ?? a.courseId) !== id))
    } catch (err) { console.error(err) }
  }
  const handleAssignCourse = async (courseId) => {
    if (!assignSelected.size) return
    const workerIds = [...assignSelected].filter(wid => !assignments.find(a => (a.course_id ?? a.courseId) === courseId && String(a.worker_id ?? a.workerId) === String(wid)))
    if (!workerIds.length) { setAssignModal(null); setAssignSelected(new Set()); return }
    try {
      await trainingAPI.assign(courseId, workerIds)
      const updated = await trainingAPI.getAssignments()
      setAssignments(updated)

      const course = training.find(c => c.id === courseId)
      if (course) {
        await Promise.all(workerIds.map(wid =>
          addDoc(collection(db, 'notifications'), {
            recipientId: String(wid),
            recipientType: 'worker',
            type: 'training',
            message: `New training assigned: ${course.title}`,
            read: false,
            createdAt: serverTimestamp(),
          })
        ))
      }
    } catch (err) { console.error(err) }
    setAssignModal(null); setAssignSelected(new Set())
  }
  const handleUnassignCourse = async assignmentId => {
    try {
      await trainingAPI.unassign(assignmentId)
      setAssignments(prev => prev.filter(a => a.id !== assignmentId))
    } catch (err) { console.error(err) }
  }
  const toggleAssignWorker = (wid) => {
    setAssignSelected(prev => {
      const next = new Set(prev)
      next.has(wid) ? next.delete(wid) : next.add(wid)
      return next
    })
  }

  const handleEquipmentSubmit = async e => {
    e.preventDefault()
    const { title, price, details, images } = equipmentForm
    if (!title.trim() || !price.trim() || !details.trim()) { setEquipmentStatus('Complete all fields.'); return }
    if (!images.length) { setEquipmentStatus('Add at least one image.'); return }
    const safeSlug = ensureUniqueSlug(slugify(title.trim()) || 'item', existingSlugs)
    try {
      const base64Images = await Promise.all(images.map(f => new Promise((res, rej) => { const r = new FileReader(); r.onloadend = () => res(r.result); r.onerror = rej; r.readAsDataURL(f) })))
      const payload = { slug: safeSlug, name: title.trim(), price: price.trim(), description: details.trim(), images: base64Images, createdAt: new Date().toISOString() }
      const updated = [...uploadedItems, payload]
      setUploadedItems(updated); localStorage.setItem(EQUIPMENT_KEY, JSON.stringify(updated))
      onAdd(payload); setEquipmentForm({ ...EMPTY_EQUIP }); setEquipmentStatus(`Added "${payload.name}"!`)
    } catch { setEquipmentStatus('Failed to process images.') }
  }
  const handleEquipmentDelete = slug => {
    const updated = uploadedItems.filter(i => i.slug !== slug)
    setUploadedItems(updated); localStorage.setItem(EQUIPMENT_KEY, JSON.stringify(updated))
  }

  const handleAddProject = async e => {
    e.preventDefault()
    const { title, description } = projectForm
    if (!title.trim() || !description.trim()) { setProjectStatus('Title and description required.'); return }
    try {
      const created = await projectsAPI.create({ ...projectForm, title: title.trim(), description: description.trim() })
      setProjects(prev => [...prev, created])
      setProjectForm({ ...EMPTY_PROJECT }); setProjectStatus(`Project "${title.trim()}" created!`)
    } catch (err) { setProjectStatus('Error: ' + err.message) }
  }
  const handleDeleteProject = async id => {
    try {
      await projectsAPI.delete(id)
      setProjects(prev => prev.filter(p => p.id !== id))
      if (activeProject?.id === id) setActiveProject(null)
    } catch (err) { console.error(err) }
  }
  const handleProjectStatusChange = async (id, status) => {
    try {
      const updated = await projectsAPI.update(id, { status })
      setProjects(prev => prev.map(p => p.id === id ? updated : p))
      if (activeProject?.id === id) setActiveProject(updated)
    } catch (err) { console.error(err) }
  }
  const handleAdminComment = async e => {
    e.preventDefault()
    if (!adminCommentText.trim() || !activeProject) return
    const comment = { id: genId(), text: adminCommentText.trim(), by: 'Admin', role: 'admin', at: new Date().toISOString() }
    const newComments = [...(activeProject.comments || []), comment]
    try {
      const updated = await projectsAPI.update(activeProject.id, { comments: newComments })
      setProjects(prev => prev.map(p => p.id === activeProject.id ? updated : p))
      setActiveProject(updated)
      setAdminCommentText('')
    } catch (err) { console.error(err) }
  }

  const handleAddSale = async e => {
    e.preventDefault()
    const { client, product, amount } = saleForm
    if (!client.trim() || !product.trim() || !amount.trim()) { setSaleStatus('Client, product and amount required.'); return }
    const st = saleForm.status || 'quote'
    const prefix = (st === 'quote' || st === 'pending' || st === 'accepted') ? 'QUOTE' : (st === 'invoice' || st === 'paid') ? 'INV' : 'SALE'
    const ref = `${prefix}-${Date.now().toString(36).toUpperCase()}`
    try {
      const created = await salesAPI.create({ ...saleForm, status: st, reference: ref, client: client.trim(), product: product.trim(), amount: amount.trim() })
      setSales(prev => [...prev, created])
      const nextStatus = st === 'invoice' || st === 'paid' || st === 'confirmed' || st === 'delivered' ? 'invoice' : 'quote'
      setSaleForm({ ...EMPTY_SALE, status: nextStatus }); setSaleStatus(`${ref} added!`)
    } catch (err) { setSaleStatus('Error: ' + err.message) }
  }
  const handleDeleteSale = async id => {
    try {
      await salesAPI.delete(id)
      setSales(prev => prev.filter(s => s.id !== id))
      setSelectedSale(prev => (prev && prev.id === id ? null : prev))
    } catch (err) { console.error(err) }
  }
  const handleSaleStatusChange = async (id, status) => {
    try {
      const updated = await salesAPI.update(id, { status })
      setSales(prev => prev.map(s => s.id === id ? updated : s))
      setSelectedSale(prev => (prev && prev.id === id ? updated : prev))
    } catch (err) { console.error(err) }
  }
  const handleSaveSaleDetail = async (patch) => {
    if (!selectedSale) return null
    const updated = await salesAPI.update(selectedSale.id, patch)
    setSales(prev => prev.map(s => s.id === selectedSale.id ? updated : s))
    setSelectedSale(updated)
    return updated
  }
  const handleDeleteSaleAndClose = async (id) => {
    try {
      await salesAPI.delete(id)
      setSales(prev => prev.filter(s => s.id !== id))
      setSelectedSale(prev => (prev && prev.id === id ? null : prev))
    } catch (err) { console.error(err) }
  }

  const handleSelectChatSession = (sessionId) => {
    setChatActiveSession(sessionId)
    setChatSessionMsgs([])
  }

  const handleSendReply = async e => {
    e.preventDefault()
    if (!adminReply.trim() || !chatActiveSession) return
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000'
    try {
      await fetch(`${API_BASE}/visitors/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: chatActiveSession, sender: 'admin', name: 'Support Agent', text: adminReply.trim() }),
      })
      setAdminReply('')
    } catch (err) { console.error(err) }
  }

  const handleItReply = async e => {
    e.preventDefault()
    if (!itReply.trim() || !itActiveWorker) return
    try {
      await addDoc(collection(db, 'it_support_messages'), {
        workerId: itActiveWorker,
        workerName: itConversations.find(c => c.workerId === itActiveWorker)?.workerName || '',
        text: itReply.trim(),
        sender: 'admin',
        agentName: 'IT Team',
        timestamp: serverTimestamp()
      })
      setItReply('')
    } catch (err) { console.error(err) }
  }

  const handleTimeOffAction = async (requestId, action, note = '') => {
    try {
      const request = adminTimeOffRequests.find(r => r.id === requestId)
      await updateDoc(doc(db, 'time_off_requests', requestId), {
        status: action,
        adminNote: note,
        reviewedAt: serverTimestamp()
      })

      if (request?.workerId) {
        await addDoc(collection(db, 'notifications'), {
          recipientId: String(request.workerId),
          recipientType: 'worker',
          type: 'timeoff',
          message: action === 'approved'
            ? `Your time-off request (${request.type || 'leave'}) has been approved.`
            : `Your time-off request (${request.type || 'leave'}) was denied.`,
          read: false,
          createdAt: serverTimestamp(),
        })
      }
      setTimeOffActionMsg(`Request ${action}.`)
      setTimeout(() => setTimeOffActionMsg(''), 3000)
    } catch (err) { console.error(err) }
  }

  const navigate = tab => {
    setActiveTab(tab)
    localStorage.setItem('admin_tab', tab)
    setSidebarOpen(false)
    if (tab === 'quotes') setSaleForm({ ...EMPTY_SALE, status: 'quote' })
    else if (tab === 'invoices') setSaleForm({ ...EMPTY_SALE, status: 'invoice' })
    else if (tab === 'sell') setSaleForm({ ...EMPTY_SALE, status: 'quote' })
  }

  const dismissAdminNotif = async (id) => {
    try { await deleteDoc(doc(db, 'notifications', id)) } catch (err) { console.error(err) }
  }
  return {
    handleLogin,
    handleLogout,
    handleAddWorker,
    handleDeleteWorker,
    toggleWorkerStatus,
    toggleChatAccess,
    openEditWorker,
    handleSaveWorker,
    handleAddTraining,
    handleDeleteTraining,
    handleAssignCourse,
    handleUnassignCourse,
    toggleAssignWorker,
    handleEquipmentSubmit,
    handleEquipmentDelete,
    handleAddProject,
    handleDeleteProject,
    handleProjectStatusChange,
    handleAdminComment,
    handleAddSale,
    handleDeleteSale,
    handleSaleStatusChange,
    handleSaveSaleDetail,
    handleDeleteSaleAndClose,
    handleSelectChatSession,
    handleSendReply,
    handleItReply,
    handleTimeOffAction,
    navigate,
    dismissAdminNotif
  }
}
